import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Truck, Package, Clock, AlertTriangle, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import ContactBlock from "../../components/contact/ContactBlock";

const ShippingPolicyScreen = () => {
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const sections = [
		{
			icon: <Truck className="w-8 h-8 text-primary" />,
			title: "Free Shipping (Canada)",
			content: [
				"We are delighted to offer free standard shipping Canada-wide, even to remote locations.",
				"Sales tax is applied based on the province or territory where your order is shipped.",
				"For international shipping, costs are based on your order's weight and the chosen delivery method. Add items to your cart and proceed to checkout to see costs.",
			],
		},
		{
			icon: <Package className="w-8 h-8 text-primary" />,
			title: "Delivery Location",
			content: [
				"Our products are currently available for delivery to addresses in Canada and the United States of America.",
				`If you're outside these areas, drop us a line at <a
								href="mailto:info@offcutshades.com"
								class="link"
							>
								info@offcutshades.com
							</a> for assistance.`,
			],
		},
		{
			icon: <Clock className="w-8 h-8 text-primary" />,
			title: "Delivery Time",
			content: [
				"Delivery times are estimates starting from the ship out date, not the order date. These estimates are subject to the acceptance and approval of your order.",
				"We don't ship out on Saturdays or Sundays. Ship out dates may also vary due to carrier practices, delivery location, shipping method, and the items ordered.",
				"Products might be delivered in multiple shipments.",
			],
		},
		{
			icon: <AlertTriangle className="w-8 h-8 text-primary" />,
			title: "Damaged Items in Transport",
			content: [
				`If there's any damage to products or packaging upon delivery, let us know at <a
								href="mailto:info@offcutshades.com"
								class="link"
							>
								info@offcutshades.com
							</a> within seven (7) days of receiving your order.`,
				"Unfortunately, we can't replace or refund damaged goods after this period.",
				`Remember, a picture speaks volumes when submitting your claim.`,
			],
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-50">
			<Helmet>
				<title>Shipping Policy | Off Cut Shades</title>
				<meta
					name="description"
					content="Off Cut Shades shipping policy. Products are shipped from Toronto, Canada with fast processing times for our custom window shades in Canada."
				/>
				<link
					rel="canonical"
					href="https://offcutshades.com/help/shipping-policy"
				/>
			</Helmet>

			<main className="container mx-auto px-4 py-12">
				{/* Hero Section */}
				<motion.div
					className="text-center mb-16"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
				>
					<motion.div
						className="flex justify-center mb-6"
						initial={{ scale: 0 }}
						animate={{ scale: 1 }}
						transition={{ ...bounceTransition, delay: 0.2 }}
					>
						<img
							src="/images/newHome/Shipping-Information-80x80.png"
							alt="Shipping Information"
							className="w-20 h-20"
						/>
					</motion.div>

					<motion.h1
						className="relative z-10 text-5xl md:text-7xl font-bold mb-6 leading-tight"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.3 }}
					>
						Shipping{" "}
						<span className="relative">
							<span className="text-primary">Information</span>
						</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-600 max-w-3xl mx-auto"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.4 }}
					>
						All of our fantastic products come directly from our factory in
						Toronto, Ontario, Canada. We’re proud to say that we provide one of
						the fastest processing times for window shades in Canada: your order
						is typically ready to be shipped out in just two business days.
					</motion.p>
				</motion.div>

				{/* Policy Sections */}
				<div className="space-y-8">
					{sections.map((section, index) => (
						<motion.div
							key={index}
							className="bg-white rounded-3xl shadow-lg overflow-hidden"
							initial={{ opacity: 0, y: 50 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: index * 0.1 }}
							viewport={{ once: true }}
						>
							<div className="p-8">
								<div className="flex items-center gap-4 mb-6">
									{section.icon}
									<h2 className="text-2xl font-bold text-gray-900">
										{section.title}
									</h2>
								</div>
								<div className="space-y-4">
									{section.content.map((paragraph, pIndex) => (
										<p key={pIndex} className="text-gray-600">
											<span dangerouslySetInnerHTML={{ __html: paragraph }} />
										</p>
									))}
								</div>
							</div>
						</motion.div>
					))}
				</div>
			</main>

			{/* Contact Support Section */}
			<ContactBlock />
		</div>
	);
};

export default ShippingPolicyScreen;
